import Collection from '@model-core/core/Collection';
import { getApiErrorMessage } from '@/lib/apiError';
import { companyUrn } from '@/models/entities/CompanyModel';
import MemberModel, { memberUrn } from '@/models/entities/MemberModel';
import TeamMemberModel from '@/models/entities/TeamMemberModel';
import TeamModel, { teamUrn } from '@/models/entities/TeamModel';
import { userUrn } from '@/models/entities/UserModel';
import { TEAM_ROLE, TEAM_STATUS } from '@/models/references/references';
import { resolveReference } from '@/stores/useReferenceOptions';

/**
 * Rattache un utilisateur à une société avec son rôle (POST /members : droit du Dirigeant de cette société), puis, avec
 * teamId, l'ajoute à cette équipe comme équipier (POST /team_members). Deux enregistrements : si le second est refusé,
 * le rattachement reste ; renvoie alors le motif du refus (null sinon).
 */
export async function createMember(
  userId: id | number,
  companyId: number,
  companyRole: string,
  teamId: number | null = null,
): Promise<string | null> {
  const { model: member } = await new MemberModel().save(
    {
      user: userUrn(userId),
      company: companyUrn(companyId),
      companyRole: (await resolveReference('companyRoles', companyRole)).urn,
    },
    { silentError: true },
  );
  if (teamId === null) return null;

  try {
    await new TeamMemberModel().save(
      {
        team: teamUrn(teamId),
        member: memberUrn(member.id),
        teamRole: (await resolveReference('teamRoles', TEAM_ROLE.TEAMMATE)).urn,
      },
      { silentError: true },
    );
    return null;
  } catch (e) {
    return getApiErrorMessage(e);
  }
}

/** Équipes actives d'une société, par nom : celles où un nouvel employé peut entrer */
export async function loadActiveTeams(companyId: string): Promise<TeamModel[]> {
  const active = await resolveReference('teamStatuses', TEAM_STATUS.ACTIVE);
  const { collection } = await new Collection(TeamModel)
    .setFilter('company', companyId)
    .setFilter('teamStatus', String(active.id))
    .setSort('name')
    .list();
  return collection.models;
}
